import { useCallback, useEffect, useState } from 'react';

interface UseDialogStepsProps<T extends string> {
  steps: T[];
  onComplete: () => void;
  onCancel: () => void;
  onStepChange?: (newStep?: T) => void;
  initialStep?: T;
}

export const useDialogSteps = <T extends string>({
  steps,
  onComplete,
  onCancel,
  onStepChange,
  initialStep,
}: UseDialogStepsProps<T>) => {
  const resolveInitialIndex = useCallback(() => {
    if (!initialStep) {
      return 0;
    }

    const index = steps.indexOf(initialStep);
    return index >= 0 ? index : 0;
  }, [initialStep, steps]);

  const [stepIndex, setStepIndex] = useState<number>(resolveInitialIndex);

  const step: T = steps[stepIndex];

  const onPrev = () => {
    if (stepIndex === 0) return onCancel();
    setStepIndex((i) => i - 1);
  };

  const onNext = () => {
    if (stepIndex === steps.length - 1) return onComplete();
    setStepIndex((i) => i + 1);
  };

  const goToStep = useCallback(
    (targetStep: T) => {
      const index = steps.indexOf(targetStep);
      if (index >= 0) {
        setStepIndex(index);
      }
    },
    [steps],
  );

  const reset = useCallback(() => {
    setStepIndex(resolveInitialIndex());
  }, [resolveInitialIndex]);

  useEffect(() => {
    if (onStepChange) {
      onStepChange(step);
    }
  }, [step]);

  return {
    step,
    onPrev,
    onNext,
    goToStep,
    reset,
  };
};
